<template>
  <div class="guides-page">
    <!-- 面包屑导航 -->
    <div class="breadcrumb-section">
      <div class="container mx-auto px-4 py-4">
        <nav class="flex" aria-label="Breadcrumb">
          <ol class="inline-flex items-center space-x-1 md:space-x-3">
            <li class="inline-flex items-center">
              <router-link to="/" class="inline-flex items-center text-sm font-medium text-gray-400 hover:text-white">
                <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"></path>
                </svg>
                Home
              </router-link>
            </li>
            <li>
              <div class="flex items-center">
                <svg class="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"></path>
                </svg>
                <span class="ml-1 text-sm font-medium text-white md:ml-2">Gaming Guides</span>
              </div>
            </li>
          </ol>
        </nav>
      </div>
    </div>

    <!-- 英雄区域 -->
    <div class="hero-section">
      <div class="container mx-auto px-4 py-16">
        <div class="text-center">
          <h1 class="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Gaming <span class="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-600">Guides</span>
          </h1>
          <p class="text-xl lg:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
            Master your gaming skills with our comprehensive guides, tutorials, and expert strategies. From beginner tips to advanced techniques, we've got everything you need to level up your gaming experience.
          </p>
        </div>
      </div>
    </div>

    <!-- 指南分类 -->
    <div class="categories-section">
      <div class="container mx-auto px-4 py-16">
        <h2 class="text-3xl lg:text-4xl font-bold text-white text-center mb-12">Guide Categories</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div class="category-card bg-gray-800 rounded-lg p-6 hover:transform hover:scale-105 transition-all duration-300">
            <div class="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-white mb-3 text-center">Beginner Guides</h3>
            <p class="text-gray-400 text-center mb-4">Perfect for newcomers to browser gaming. Learn the basics, understand controls, and get comfortable with different game genres.</p>
            <ul class="text-gray-300 text-sm space-y-2 mb-4">
              <li>• Getting started with browser games</li>
              <li>• Understanding game controls</li>
              <li>• System requirements and optimization</li>
              <li>• Creating your gaming profile</li>
            </ul>
          </div>

          <div class="category-card bg-gray-800 rounded-lg p-6 hover:transform hover:scale-105 transition-all duration-300">
            <div class="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-white mb-3 text-center">Strategy Guides</h3>
            <p class="text-gray-400 text-center mb-4">Advanced strategies for competitive gaming. Learn winning techniques, optimal builds, and pro-level tactics.</p>
            <ul class="text-gray-300 text-sm space-y-2 mb-4">
              <li>• Advanced gameplay strategies</li>
              <li>• Character builds and optimization</li>
              <li>• Competitive gaming tactics</li>
              <li>• Team coordination tips</li>
            </ul>
          </div>

          <div class="category-card bg-gray-800 rounded-lg p-6 hover:transform hover:scale-105 transition-all duration-300">
            <div class="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-white mb-3 text-center">Performance Tips</h3>
            <p class="text-gray-400 text-center mb-4">Optimize your gaming experience with performance tweaks, browser settings, and hardware recommendations.</p>
            <ul class="text-gray-300 text-sm space-y-2 mb-4">
              <li>• Browser optimization for gaming</li>
              <li>• Network and connection tips</li>
              <li>• Hardware recommendations</li>
              <li>• Troubleshooting common issues</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- 热门指南 -->
    <div class="popular-guides-section">
      <div class="container mx-auto px-4 py-16">
        <h2 class="text-3xl lg:text-4xl font-bold text-white text-center mb-12">Popular Guides</h2>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <article class="guide-article bg-gray-800 rounded-lg overflow-hidden shadow-lg">
            <div class="p-6">
              <div class="flex items-center text-sm text-gray-400 mb-3">
                <span class="bg-green-600 text-white px-2 py-1 rounded-full text-xs mr-3">Beginner</span>
                <span>Updated December 2024</span>
              </div>
              <h3 class="text-2xl font-bold text-white mb-4">Complete Beginner's Guide to Browser Gaming</h3>
              <p class="text-gray-300 mb-4 leading-relaxed">
                New to browser gaming? This comprehensive guide covers everything you need to know to get started. Learn about different game genres, understand how browser games work, and discover tips for the best gaming experience.
              </p>
              <div class="space-y-3 text-gray-300 mb-6">
                <div class="flex items-start">
                  <span class="text-green-400 mr-2">✓</span>
                  <span>Understanding browser game technology and requirements</span>
                </div>
                <div class="flex items-start">
                  <span class="text-green-400 mr-2">✓</span>
                  <span>Choosing the right games for your skill level</span>
                </div>
                <div class="flex items-start">
                  <span class="text-green-400 mr-2">✓</span>
                  <span>Setting up your browser for optimal gaming performance</span>
                </div>
                <div class="flex items-start">
                  <span class="text-green-400 mr-2">✓</span>
                  <span>Creating and customizing your gaming profile</span>
                </div>
              </div>
              <button class="bg-green-600 hover:bg-green-700 text-white py-2 px-6 rounded-lg transition-colors duration-200">
                Read Full Guide
              </button>
            </div>
          </article>

          <article class="guide-article bg-gray-800 rounded-lg overflow-hidden shadow-lg">
            <div class="p-6">
              <div class="flex items-center text-sm text-gray-400 mb-3">
                <span class="bg-blue-600 text-white px-2 py-1 rounded-full text-xs mr-3">Intermediate</span>
                <span>Updated December 2024</span>
              </div>
              <h3 class="text-2xl font-bold text-white mb-4">Mastering Puzzle Games: Advanced Strategies</h3>
              <p class="text-gray-300 mb-4 leading-relaxed">
                Take your puzzle-solving skills to the next level with advanced techniques and strategies. Learn pattern recognition, efficient solving methods, and time management for competitive puzzle gaming.
              </p>
              <div class="space-y-3 text-gray-300 mb-6">
                <div class="flex items-start">
                  <span class="text-blue-400 mr-2">✓</span>
                  <span>Advanced pattern recognition techniques</span>
                </div>
                <div class="flex items-start">
                  <span class="text-blue-400 mr-2">✓</span>
                  <span>Speed-solving strategies for competitive play</span>
                </div>
                <div class="flex items-start">
                  <span class="text-blue-400 mr-2">✓</span>
                  <span>Mental preparation and focus techniques</span>
                </div>
                <div class="flex items-start">
                  <span class="text-blue-400 mr-2">✓</span>
                  <span>Analyzing and learning from mistakes</span>
                </div>
              </div>
              <button class="bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-lg transition-colors duration-200">
                Read Full Guide
              </button>
            </div>
          </article>

          <article class="guide-article bg-gray-800 rounded-lg overflow-hidden shadow-lg">
            <div class="p-6">
              <div class="flex items-center text-sm text-gray-400 mb-3">
                <span class="bg-purple-600 text-white px-2 py-1 rounded-full text-xs mr-3">Technical</span>
                <span>Updated December 2024</span>
              </div>
              <h3 class="text-2xl font-bold text-white mb-4">Optimizing Your Browser for Gaming Performance</h3>
              <p class="text-gray-300 mb-4 leading-relaxed">
                Maximize your gaming performance with browser optimization techniques. Learn about hardware acceleration, memory management, and network optimization for the smoothest gaming experience.
              </p>
              <div class="space-y-3 text-gray-300 mb-6">
                <div class="flex items-start">
                  <span class="text-purple-400 mr-2">✓</span>
                  <span>Enabling hardware acceleration for better graphics</span>
                </div>
                <div class="flex items-start">
                  <span class="text-purple-400 mr-2">✓</span>
                  <span>Managing browser memory and cache effectively</span>
                </div>
                <div class="flex items-start">
                  <span class="text-purple-400 mr-2">✓</span>
                  <span>Network optimization for reduced latency</span>
                </div>
                <div class="flex items-start">
                  <span class="text-purple-400 mr-2">✓</span>
                  <span>Troubleshooting common performance issues</span>
                </div>
              </div>
              <button class="bg-purple-600 hover:bg-purple-700 text-white py-2 px-6 rounded-lg transition-colors duration-200">
                Read Full Guide
              </button>
            </div>
          </article>

          <article class="guide-article bg-gray-800 rounded-lg overflow-hidden shadow-lg">
            <div class="p-6">
              <div class="flex items-center text-sm text-gray-400 mb-3">
                <span class="bg-red-600 text-white px-2 py-1 rounded-full text-xs mr-3">Advanced</span>
                <span>Updated December 2024</span>
              </div>
              <h3 class="text-2xl font-bold text-white mb-4">Competitive Gaming: Tournament Strategies</h3>
              <p class="text-gray-300 mb-4 leading-relaxed">
                Ready for competitive play? Learn advanced tournament strategies, mental preparation techniques, and how to perform under pressure in competitive gaming environments.
              </p>
              <div class="space-y-3 text-gray-300 mb-6">
                <div class="flex items-start">
                  <span class="text-red-400 mr-2">✓</span>
                  <span>Pre-tournament preparation and practice routines</span>
                </div>
                <div class="flex items-start">
                  <span class="text-red-400 mr-2">✓</span>
                  <span>Managing pressure and performance anxiety</span>
                </div>
                <div class="flex items-start">
                  <span class="text-red-400 mr-2">✓</span>
                  <span>Analyzing opponent strategies and adapting</span>
                </div>
                <div class="flex items-start">
                  <span class="text-red-400 mr-2">✓</span>
                  <span>Building consistency in competitive play</span>
                </div>
              </div>
              <button class="bg-red-600 hover:bg-red-700 text-white py-2 px-6 rounded-lg transition-colors duration-200">
                Read Full Guide
              </button>
            </div>
          </article>
        </div>
      </div>
    </div>

    <!-- 社区贡献 -->
    <div class="community-contribution-section">
      <div class="container mx-auto px-4 py-16">
        <div class="bg-gray-800 rounded-lg p-8 text-center">
          <h2 class="text-3xl font-bold text-white mb-6">Contribute to Our Guide Library</h2>
          <p class="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Have expertise in a particular game or gaming technique? Share your knowledge with our community by contributing guides, tips, and tutorials.
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <button class="bg-blue-600 hover:bg-blue-700 text-white py-3 px-8 rounded-lg font-semibold transition-colors duration-200">
              Submit a Guide
            </button>
            <button class="bg-gray-700 hover:bg-gray-600 text-white py-3 px-8 rounded-lg font-semibold transition-colors duration-200">
              Join Our Writers Program
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'

export default {
  name: 'Guides',
  setup() {
    // 这里可以添加指南相关的逻辑
    return {
      // 返回响应式数据
    }
  }
}
</script>

<style scoped>
.guides-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
}

.hero-section {
  background: linear-gradient(135deg, rgba(26, 26, 46, 0.9) 0%, rgba(22, 33, 62, 0.9) 100%);
  backdrop-filter: blur(10px);
}

.category-card {
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.category-card:hover {
  border-color: rgba(59, 130, 246, 0.5);
  transform: translateY(-5px);
}

.guide-article {
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.guide-article:hover {
  border-color: rgba(59, 130, 246, 0.3);
  transform: translateY(-2px);
}

/* 响应式设计 */
@media (max-width: 640px) {
  .hero-section h1 {
    font-size: 3rem;
  }
}
</style>
